import React, { useEffect } from "react";

import countries from "i18n-iso-countries";
import esLocale from "i18n-iso-countries/langs/es.json";
import enLocale from "i18n-iso-countries/langs/en.json";
import frLocale from "i18n-iso-countries/langs/fr.json";
import itLocale from "i18n-iso-countries/langs/it.json";
import SelectField from "../../ui/components/SelectField/SelectField";
import InputField from "../../ui/components/InputField/InputField";
import useCustomerContext from "../../customers/hooks/useCustomerContext";

const isDNIValid = (value: string, t: Function) => {
    const dniRegex = /^(\d{8})([A-Z])$/i;
    const match = value.match(dniRegex);
    if (!match) return t("booking.invalidDNIFormat");  // Usar la traducción

    const number = parseInt(match[1], 10);
    const letter = match[2].toUpperCase();
    const validLetters = "TRWAGMYFPDXBNJZSQVHLCKE";
    const expectedLetter = validLetters[number % 23];

    if (letter !== expectedLetter) return t("booking.invalidDNILetter");  // Usar la traducción
    return true;
};

const isNIEValid = (value: string, t: Function) => {
    const nieRegex = /^[XYZ]\d{7}[A-Z]$/i;
    const match = value.match(nieRegex);
    if (!match) return t("booking.invalidNIEFormat");  // Usar la traducción

    const letter = value.slice(-1).toUpperCase();
    let number = value.slice(0, -1).toUpperCase();
    const prefix = { X: "0", Y: "1", Z: "2" };
    number = prefix[number[0]] + number.slice(1);
    const validLetters = "TRWAGMYFPDXBNJZSQVHLCKE";
    const expectedLetter = validLetters[parseInt(number, 10) % 23];

    if (letter !== expectedLetter) return t("booking.invalidNIELetter");  // Usar la traducción
    return true;
};

const BookingNationalInputs = ({ t, register, errors, watch, setValue }) => {
    const {languageId} = useCustomerContext();
    const [locale, setLocale] = React.useState("en");
    countries.registerLocale(enLocale);
    useEffect(() => {
        switch (languageId) {
            case 1:
                countries.registerLocale(esLocale);
                setLocale("es");
                break;
            case 2:
                countries.registerLocale(enLocale);
                setLocale("en");
                break;
            case 3:
                countries.registerLocale(frLocale);
                setLocale("fr");
                break;
            case 4:
                countries.registerLocale(itLocale);
                setLocale("it");
                break;
        }
    }, [languageId]);

    const nationality = watch("nationality");
    const documentType = watch("documentType");

    const documentOptions = nationality === "ES"
        ? [
            { value: "DNI", label: "DNI" },
        ]
        : [
            { value: "NIE", label: "NIE" },
            { value: "PAS", label: t("booking.passport") },
        ];

    useEffect(() => {
        setValue("documentType", "");
        setValue("invoicePassportCif", "");
    }, [nationality]);

    return (
        <div className="lg:flex lg:space-x-5">
            <div className="lg:w-1/3 mb-2">
                <SelectField
                    label={t("booking.nationality")}
                    error={errors.nationality?.message}
                    {...register("nationality", {
                        required: t("common.feedback.field_required"),
                    })}
                >
                    <option value="">{t("")}</option>
                    {Object.entries(countries.getNames(locale))
                        .map(([code, name]) => ({ code, name: name as string }))
                        .sort((a, b) => a.name.localeCompare(b.name)).map(({ code, name }) => (
                        <option key={code} value={code}>
                            {name}
                        </option>
                    ))}
                </SelectField>
            </div>
            <div className="lg:w-1/3 mb-2">
                <SelectField
                    label={t("booking.documentType")}
                    error={errors.documentType?.message}
                    {...register("documentType", {
                        required: t("common.feedback.field_required"),
                    })}
                >
                    <option value="">{t("")}</option>
                    {documentOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                            {opt.label}
                        </option>
                    ))}
                </SelectField>
            </div>
            <div className="lg:w-1/3 mb-2">
                <InputField
                    label={t("booking.invoicePassportCif")}
                    defaultValue=""
                    {...register("invoicePassportCif", {
                        required: t("common.feedback.field_required"),
                        validate:
                            documentType === "DNI"
                                ? (value) => isDNIValid(value, t)
                                : documentType === "NIE"
                                    ? (value) => isNIEValid(value, t)
                                    : undefined,
                    })}
                    error={errors.invoicePassportCif?.message}
                />
            </div>
        </div>
    );
};

export default BookingNationalInputs;
